import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, ChefHat, User, LogOut } from "lucide-react";
import { IMAGES, NAV_LINKS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";

const scrollToSection = (href) => {
  const id = href.replace("#", "");
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout, cartCount } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    if (location.pathname !== "/") navigate("/");
  };

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? "bg-cream/90 backdrop-blur-xl shadow-[0_1px_0_rgba(74,93,35,0.08)]"
            : "bg-transparent"
        }`}
      >
        <div className="section-container flex items-center justify-between h-16 lg:h-20">
          <a
            href="#home"
            onClick={(e) => { e.preventDefault(); if (location.pathname === "/") scrollToSection("#home"); else navigate("/"); }}
            className="flex items-center gap-2"
            data-testid="navbar-logo"
          >
            <img src={IMAGES.logo} alt="Swadishes" className="h-10 lg:h-12 rounded-md" />
          </a>

          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  if (link.href.startsWith("/")) { navigate(link.href); }
                  else if (location.pathname === "/") scrollToSection(link.href);
                  else navigate("/");
                }}
                data-testid={`nav-link-${link.href.replace("#", "").replace("/", "")}`}
                className="text-sm font-body font-medium text-sw-text-secondary hover:text-earth-green transition-colors duration-300"
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => navigate("/cart")}
                  data-testid="nav-cart-btn"
                  className="relative p-2 text-sw-text-secondary hover:text-earth-green transition-colors"
                >
                  <ShoppingBag size={20} />
                  {cartCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-spice-terracotta text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px]" data-testid="cart-badge">
                      {cartCount}
                    </span>
                  )}
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    data-testid="nav-user-menu-btn"
                    className="flex items-center gap-2 bg-cream-secondary hover:bg-cream-tertiary rounded-full pl-2 pr-4 py-1.5 transition-all"
                  >
                    <div className="w-7 h-7 rounded-full bg-earth-green/15 flex items-center justify-center">
                      <User size={14} className="text-earth-green" />
                    </div>
                    <span className="text-sm font-medium text-sw-text max-w-[100px] truncate">{user?.name?.split(" ")[0]}</span>
                  </button>
                  <AnimatePresence>
                    {showUserMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 overflow-hidden"
                        data-testid="user-dropdown"
                      >
                        <button onClick={() => { navigate("/my-kitchen"); setShowUserMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-sw-text hover:bg-cream-secondary transition-colors" data-testid="nav-my-kitchen">
                          <ChefHat size={16} className="text-earth-green" /> My Kitchen
                        </button>
                        <button onClick={() => { navigate("/cart"); setShowUserMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-sw-text hover:bg-cream-secondary transition-colors" data-testid="nav-dropdown-cart">
                          <ShoppingBag size={16} className="text-earth-green" /> Cart {cartCount > 0 && `(${cartCount})`}
                        </button>
                        <div className="border-t border-gray-100 my-1" />
                        <button onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors" data-testid="nav-logout">
                          <LogOut size={16} /> Sign Out
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            ) : (
              <>
                <button
                  data-testid="nav-login-btn"
                  onClick={() => setShowAuth(true)}
                  className="text-sm font-body font-medium text-sw-text-secondary hover:text-earth-green transition-colors"
                >
                  Sign In
                </button>
              </>
            )}
            <button
              data-testid="nav-cta-customize"
              onClick={() => navigate("/studio")}
              className="bg-earth-green text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-earth-green-dark transition-all duration-300 shadow-lg shadow-earth-green/20 hover:shadow-xl hover:shadow-earth-green/30"
            >
              Start Customizing
            </button>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            {isAuthenticated && (
              <button onClick={() => navigate("/cart")} data-testid="mobile-cart-btn" className="relative p-2 text-sw-text-secondary">
                <ShoppingBag size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-spice-terracotta text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
            )}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 text-sw-text"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-50 bg-cream/98 backdrop-blur-xl lg:hidden"
          >
            <div className="flex h-full flex-col">
              <div className="flex h-16 items-center justify-between border-b border-gray-100/70 px-4">
                <img src={IMAGES.logo} alt="Swadishes" className="h-10 rounded-md" />
                <button onClick={() => setMobileOpen(false)} className="rounded-full border border-gray-200 p-2">
                  <X size={22} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-4 pb-8 pt-6">
                {isAuthenticated && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 rounded-3xl border border-white/70 bg-white/80 p-4 shadow-sm"
                  >
                    <p className="text-xs uppercase tracking-[0.2em] text-sw-text-muted">Signed in</p>
                    <p className="mt-2 text-xl font-heading font-700 text-sw-text">{user?.name || "Swadishes User"}</p>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <button
                        onClick={() => { setMobileOpen(false); navigate("/my-kitchen"); }}
                        className="rounded-2xl bg-cream-secondary px-4 py-3 text-left"
                        data-testid="mobile-my-kitchen"
                      >
                        <div className="text-xs uppercase tracking-[0.18em] text-sw-text-muted">Kitchen</div>
                        <div className="mt-1 text-sm font-medium text-sw-text">Saved blends</div>
                      </button>
                      <button
                        onClick={() => { setMobileOpen(false); navigate("/cart"); }}
                        className="rounded-2xl bg-cream-secondary px-4 py-3 text-left"
                        data-testid="mobile-menu-cart"
                      >
                        <div className="text-xs uppercase tracking-[0.18em] text-sw-text-muted">Cart</div>
                        <div className="mt-1 text-sm font-medium text-sw-text">{cartCount} item{cartCount === 1 ? "" : "s"}</div>
                      </button>
                    </div>
                  </motion.div>
                )}

                <div className="space-y-2">
                  {NAV_LINKS.map((link, i) => (
                    <motion.a
                      key={link.href}
                      href={link.href}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      onClick={(e) => {
                        e.preventDefault();
                        setMobileOpen(false);
                        if (link.href.startsWith("/")) setTimeout(() => navigate(link.href), 220);
                        else if (location.pathname === "/") setTimeout(() => scrollToSection(link.href), 220);
                        else navigate("/");
                      }}
                      className="flex items-center justify-between rounded-2xl border border-white/70 bg-white/80 px-4 py-4 text-base font-heading font-semibold text-sw-text shadow-sm transition-colors hover:text-earth-green"
                    >
                      <span>{link.label}</span>
                      <span className="text-xs uppercase tracking-[0.18em] text-sw-text-muted">Open</span>
                    </motion.a>
                  ))}
                </div>

                <div className="mt-6 space-y-3">
                  {!isAuthenticated ? (
                    <motion.button
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.35 }}
                      onClick={() => { setMobileOpen(false); setShowAuth(true); }}
                      className="w-full rounded-full border border-earth-green/25 px-5 py-3.5 text-sm font-medium text-earth-green"
                      data-testid="mobile-sign-in"
                    >
                      Sign In
                    </motion.button>
                  ) : (
                    <motion.button
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.35 }}
                      onClick={() => { setMobileOpen(false); handleLogout(); }}
                      className="w-full rounded-full border border-red-200 px-5 py-3.5 text-sm font-medium text-red-500"
                      data-testid="mobile-logout"
                    >
                      Sign Out
                    </motion.button>
                  )}

                  <motion.button
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.42 }}
                    onClick={() => {
                      setMobileOpen(false);
                      setTimeout(() => navigate("/studio"), 220);
                    }}
                    className="w-full rounded-full bg-earth-green px-5 py-4 text-sm font-medium text-white shadow-lg shadow-earth-green/20"
                  >
                    Start Customizing
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
    </>
  );
};
